import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
import { getApiEndpointsForDomain } from '../utils/apiUtils';
import { isProductionDomain } from '../config/environment';

interface User {
  id: number;
  email: string;
  phone?: string;
  is_admin?: boolean;
}

interface AuthContextType {
  user: User | null;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, phone?: string, recaptchaToken?: string) => Promise<void>;
  logout: () => void;
  isLoading: boolean;
  error: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const logout = () => {
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  useEffect(() => {
    // Set up axios interceptor for handling 401 responses
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          // Token expired or invalid
          logout();
        }
        return Promise.reject(error);
      }
    );

    const initializeAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          // Set the token in axios headers
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          
          // CRITICAL FIX: Always use relative URL for production domain
          let meEndpoint;
          if (isProductionDomain()) {
            meEndpoint = '/api/auth/me';
            console.log('🔧 AuthContext: Using relative URL for production domain');
          } else {
            const endpoints = getApiEndpointsForDomain();
            meEndpoint = endpoints.ME;
            console.log('🔧 AuthContext: Using dynamic endpoint for other domain');
          }
          
          // Debug: Log the API endpoint being used
          console.log('AuthContext: Validating token with endpoint:', meEndpoint);
          console.log('AuthContext: Current protocol:', window.location.protocol);
          console.log('AuthContext: Current hostname:', window.location.hostname);
          
          // Validate token by making a request to get current user data
          if (isProductionDomain()) {
            // Use fetch for production domain to avoid any axios issues
            console.log('🔧 AuthContext: Using fetch for production domain');
            const response = await fetch(meEndpoint, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });
            if (response.ok) {
              const userData = await response.json();
              setUser(userData);
            } else {
              throw new Error(`HTTP ${response.status}`);
            }
          } else {
            // Use axios for other domains
            const response = await axios.get(meEndpoint);
            setUser(response.data);
          }
        } catch (error) {
          console.error('Token validation failed:', error);
          // Token is invalid, remove it
          logout();
        }
      }
      setIsLoading(false);
    };

    initializeAuth();

    // Cleanup interceptor on unmount
    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  const login = async (email: string, password: string) => {
    setError(null);
    
    // CRITICAL FIX: Always use relative URL for HTTPS or production domain
    const isProdDomain = isProductionDomain();
    const isHttps = window.location.protocol === 'https:';
    
    let loginEndpoint;
    if (isProdDomain || isHttps) {
      loginEndpoint = '/api/auth/login';
      console.log('🔧 Login: Using relative URL for HTTPS/production domain');
    } else {
      const endpoints = getApiEndpointsForDomain();
      loginEndpoint = endpoints.LOGIN;
      console.log('🔧 Login: Using dynamic endpoint for other domain');
    }
    
    console.log('🔧 Login: Final endpoint:', loginEndpoint);
    console.log('🔧 Login: Current protocol:', window.location.protocol);
    console.log('🔧 Login: Current hostname:', window.location.hostname);
    
    const response = await axios.post(loginEndpoint, {
      email,
      password,
    });
    
    const { access_token, ...userData } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setUser(userData);
  };

  const register = async (email: string, password: string, phone?: string, recaptchaToken?: string) => {
    setError(null);
    
    // CRITICAL FIX: Always use relative URL for HTTPS or production domain
    const isProdDomain = isProductionDomain();
    const isHttps = window.location.protocol === 'https:';
    
    let registerEndpoint;
    if (isProdDomain || isHttps) {
      registerEndpoint = '/api/auth/register';
      console.log('🔧 Register: Using relative URL for HTTPS/production domain');
    } else {
      const endpoints = getApiEndpointsForDomain();
      registerEndpoint = endpoints.REGISTER;
      console.log('🔧 Register: Using dynamic endpoint for other domain');
    }
    
    console.log('🔧 Register: Final endpoint:', registerEndpoint);
    console.log('🔧 Register: Current protocol:', window.location.protocol);
    console.log('🔧 Register: Current hostname:', window.location.hostname);
    
    const response = await axios.post(registerEndpoint, {
      email,
      password,
      phone,
      recaptcha_token: recaptchaToken,
    });
    
    const { access_token, ...userData } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setUser(userData);
  };

  const value = {
    user,
    login,
    register,
    logout,
    isLoading,
    error,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};